import {
  forwardRef,
  type ButtonHTMLAttributes,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";

/**
 * The tap-scale animation is CSS-driven (`active:scale-[0.97]`) rather
 * than framer-motion's `whileTap`. Two reasons:
 *
 *   1. Button is the app's most-used interactive; keeping it CSS keeps
 *      the runtime tree quiet — framer-motion is reserved for components
 *      that need physics beyond a simple scale (FAB pulse, tab layoutId,
 *      Recap number roll-up).
 *   2. `asChild` + `Slot` collapses cleanly onto a native <button> or
 *      react-router <Link>; wrapping the Slot inside `motion.button`
 *      breaks that composition.
 *
 * The design-system §5.3 tap rule ("scale(0.98), spring-snap on release")
 * is honored by the CSS scale + `transition-transform` easing, plus the
 * native haptic tick fired on pointerdown (see §0.6). Reduced-motion is
 * respected via the global `*` reset in app.css.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-sans font-semibold whitespace-nowrap select-none " +
    "transition-[background,color,box-shadow,transform] duration-200 " +
    "active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 " +
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-white shadow-warm-1 hover:bg-primary-hover hover:shadow-warm-2 rounded-full",
        secondary:
          "bg-transparent text-ink border border-line-strong hover:border-ink-muted hover:bg-bg-sunken rounded-md",
        ghost: "bg-transparent text-ink hover:bg-bg-sunken rounded-md",
        destructive:
          "bg-danger text-white shadow-warm-1 hover:bg-[#8f2c1f] rounded-full",
      },
      size: {
        sm: "h-9 px-4 text-[13px]",
        md: "h-11 px-5 text-[15px]",
        lg: "h-13 px-6 text-[16px]",
        icon: "h-11 w-11",
      },
      block: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Fire a haptic impact on pointerdown. Defaults to `true` for primary /
   * destructive; secondary / ghost stay quiet unless opted in. Set `false`
   * to silence, or a specific style to override the default.
   */
  haptic?: boolean | "tap" | "select";
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      block,
      asChild = false,
      haptic,
      onPointerDown,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // Default: haptics on the two variants that represent state-changing
    // actions. Callers can opt in on secondary/ghost by passing haptic.
    const enabled =
      haptic === undefined
        ? variant === "primary" || variant === "destructive" || variant == null
        : haptic !== false;
    const style = haptic === "select" ? "select" : "tap";

    const handlePointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (enabled && !props.disabled) {
        if (style === "select") haptics.select();
        else haptics.tap();
      }
      onPointerDown?.(e);
    };

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, block }), className)}
        onPointerDown={handlePointerDown}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
